import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Avatar, Box, CircularProgress } from '@mui/material';
import { ZapFormService } from '../../../../core/services/zap-form.service';
import { BIO_THEMES, BUTTON_SHAPES } from '../../../../core/services/bio.service';
import useBodyClass from '../../../../hooks/useBodyClass';
import { PublicPageUnavailable } from '../../../common/public-page-unavailable/PublicPageUnavailable';

/**
 * The page a follower lands on from the DM button — /form/:slug.
 *
 * Public and session-less: whoever taps the link has no ClanConnect account, so
 * this talks only to the two JWT-exempt endpoints. It is deliberately plain —
 * one question per row, big tap targets, no branding chrome to scroll past —
 * because it is opened on a phone, inside Instagram's in-app browser, by
 * someone who is mid-scroll and will not work for it.
 */

interface ZapFormField {
    key: string;
    label: string;
    type: 'text' | 'long_text' | 'email' | 'phone' | 'instagram';
    required: boolean;
    placeholder?: string;
}

// const ACCENT = '#7C3AED';
/* Styled like the public Link-in-Bio page (PublicBioPage + BioRender, light
   theme) so a follower moving between the two sees one product. */
const THEME = BIO_THEMES.light;
const ACCENT = THEME.accent;
const RADIUS = BUTTON_SHAPES.rounded;

/* Matches the server's rules exactly (zap-forms.ts) so nothing is accepted here
   only to be refused after a round trip. International by design: an optional
   leading +, then 6–15 digits (15 is E.164's maximum), punctuation ignored. */
const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const isPhone = (v: string) => {
  const raw = v.trim();
  if (!/^\+?[0-9\s\-().]+$/.test(raw)) return false;
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 6 && digits.length <= 15;
};

const isHandle = (v: string) => /^@?[A-Za-z0-9._]{1,30}$/.test(v.trim());

/* Stops the wrong characters reaching the field at all, rather than scolding
   afterwards: letters simply cannot be typed into a phone number. */
const sanitiseFor = (type: ZapFormField['type'], value: string): string => {
  if (type === 'phone') {
    // One leading + is meaningful (country code); any other + is not.
    const cleaned = value.replace(/[^0-9+\s\-().]/g, '');
    return cleaned.replace(/(?!^)\+/g, '');
  }
  if (type === 'instagram') return value.replace(/[^A-Za-z0-9._@]/g, '');
  return value;
};

const validateField = (field: ZapFormField, value: string): string => {
  const trimmed = value.trim();
  if (field.required && !trimmed) return `${field.label} is required`;
  if (!trimmed) return '';
  if (field.type === 'email' && !isEmail(trimmed)) return 'That does not look like an email address';
  if (field.type === 'phone' && !isPhone(trimmed)) {
    return 'Include the country code, e.g. +91 98765 43210';
  }
  if (field.type === 'instagram' && !isHandle(trimmed)) return 'That should look like @yourhandle';
  return '';
};

/** Keyboard and autofill hints, so a phone offers the right thing per question. */
const inputPropsFor = (type: ZapFormField['type']) => {
    switch (type) {
        case 'email':
            return { type: 'email', inputMode: 'email' as const, autoComplete: 'email' };
        case 'phone':
            return { type: 'tel', inputMode: 'tel' as const, autoComplete: 'tel' };
        default:
            return { type: 'text', inputMode: 'text' as const, autoComplete: 'off' };
    }
};

export const PublicZapForm: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    // The DM button carries ?ig=<handle> when ClanConnect sent it (see
    // withFormPrefill in public.ts) — they came from Instagram, so asking them
    // to type the handle we already know is a wasted step.
    const [searchParams] = useSearchParams();
    const igFromLink = (searchParams.get('ig') || '').trim();
    const [form, setForm] = useState<any>(null);
    // Owner's name + avatar for the header circle (bio page, else Instagram).
    const [creator, setCreator] = useState<{ name: string; avatar_url: string } | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useBodyClass('zap-form-public-page');

    useEffect(() => {
        if (!slug) return;
        ZapFormService.getPublicForm(slug)
            .then((res: any) => {
                // if (res?.status === 1 && res?.data?.form) setForm(res.data.form);
                if (res?.status === 1 && res?.data?.form) {
                    setForm(res.data.form);
                    setCreator(res.data.creator || null);
                }
                else setError('Form not found');
            })
            .catch(() => setError('Failed to load form'))
            .finally(() => setLoading(false));
    }, [slug]);

    // Prefill any Instagram field from ?ig=, once we know the form's shape.
    useEffect(() => {
        if (!igFromLink || !form) return;
        const raw = Array.isArray(form.fields) ? form.fields : (() => {
            try { return JSON.parse(form.fields || '[]'); } catch { return []; }
        })();
        const igField = (raw as ZapFormField[]).find((f) => f.type === 'instagram');
        if (!igField) return;
        setAnswers((prev) => (prev[igField.key] ? prev : { ...prev, [igField.key]: `@${igFromLink.replace(/^@/, '')}` }));
    }, [igFromLink, form]);

    const fields: ZapFormField[] = useMemo(() => {
        const raw = form?.fields;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try { return JSON.parse(raw) || []; } catch { return []; }
    }, [form]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || !slug) return;

        /* Checked here as well as on the server: a bad answer should be a red
           line under the question that caused it, not a round trip ending in one
           message at the bottom that does not say which field is wrong. */
        const errors: Record<string, string> = {};
        fields.forEach((f) => {
            const message = validateField(f, answers[f.key] || '');
            if (message) errors[f.key] = message;
        });
        setFieldErrors(errors);
        if (Object.keys(errors).length) {
            setFormError('');
            return;
        }

        setFormError('');
        setSubmitting(true);
        try {
            const res: any = await ZapFormService.submitForm({ slug, answers, source: 'form_page' });
            if (res?.status === 1) setSubmitted(true);
            else setFormError(res?.info || 'Could not send that — please try again');
        } catch {
            setFormError('Could not send that — please try again');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (error || !form) return <PublicPageUnavailable />;

    return (
        // <Box sx={{ minHeight: '100vh', bgcolor: '#f7f7f9', px: 2, py: 4 }}>
        //     <Box sx={{ maxWidth: 520, mx: 'auto' }}>
        //         <Box sx={{ bgcolor: '#fff', borderRadius: '18px', border: '1px solid #ececf0', p: { xs: 2.5, sm: 3.5 } }}>
        // Same shell as PublicBioPage: white page, 500px bordered column, soft top fade.
        <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', background: '#fff' }}>
            <Box sx={{
                width: '500px', maxWidth: '100%', minHeight: '100vh', border: '1px solid #ececec',
                display: 'flex', flexDirection: 'column',
                background: `radial-gradient(ellipse 120% 40% at 50% 0%, ${THEME.accent}1a 0%, ${THEME.bg} 65%)`,
                color: THEME.text, px: 2.5, py: { xs: 4, md: 6 },
            }}>
                <Box sx={{ flex: 1 }}>
                    {submitted ? (
                        /* No form left to fill in, no button to press again — just
                           the confirmation, so nobody wonders whether it sent. */
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Box sx={{
                                width: 56, height: 56, borderRadius: '50%', bgcolor: `${ACCENT}0d`, border: `1.5px solid ${ACCENT}44`,
                                // bgcolor: '#f5f3ff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                            }}>
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </Box>
                            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111', marginBottom: 6 }}>
                                {form.success_message || 'Thanks! We have got your details.'}
                            </p>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 0 }}>
                                You can close this page now.
                            </p>
                        </Box>
                    ) : (
                        <>
                            {/* <p style={{ fontSize: '1.35rem', fontWeight: 800, color: '#111', marginBottom: form.description ? 6 : 18 }}>
                                {form.title || 'Leave your details'}
                            </p>
                            {!!form.description && (
                                <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 18, lineHeight: 1.55 }}>
                                    {form.description}
                                </p>
                            )} */}
                            {/* Centred heading, same type scale as the bio page's name + headline. */}
                            <Box sx={{ textAlign: 'center', mt: { xs: 0, sm: 2 }, mb: 3.5 }}>
                                {/* Avatar with accent ring — same as BioRender. Initial
                                    shows when there's no photo or it fails to load. */}
                                <Box sx={{
                                    width: 100, height: 100, borderRadius: '50%', p: '3px', mx: 'auto', mb: 2,
                                    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}88 100%)`,
                                }}>
                                    <Avatar
                                        src={creator?.avatar_url || undefined}
                                        alt={creator?.name || ''}
                                        sx={{
                                            width: '100%', height: '100%', bgcolor: THEME.bg, color: ACCENT,
                                            fontSize: 36, fontWeight: 700, border: `2.5px solid ${THEME.bg}`,
                                        }}
                                    >
                                        {(creator?.name || form.title || '?')[0].toUpperCase()}
                                    </Avatar>
                                </Box>
                                {!!creator?.name && (
                                    <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: THEME.text, opacity: 0.55, margin: '0 0 6px' }}>
                                        {creator.name}
                                    </p>
                                )}
                                <p style={{ fontSize: '1.5rem', fontWeight: 800, color: THEME.text, letterSpacing: '-0.4px', lineHeight: 1.2, margin: 0 }}>
                                    {form.title || 'Leave your details'}
                                </p>
                                {!!form.description && (
                                    <p style={{ fontSize: '0.875rem', color: THEME.text, opacity: 0.6, lineHeight: 1.6, maxWidth: 320, margin: '6px auto 0' }}>
                                        {form.description}
                                    </p>
                                )}
                            </Box>

                            <form onSubmit={handleSubmit} noValidate>
                                {fields.map((field) => (
                                    <Box key={field.key} sx={{ mb: 2 }}>
                                        <label
                                            htmlFor={`zf_${field.key}`}
                                            style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}
                                        >
                                            {field.label}
                                            {field.required && <span style={{ color: '#ef4444' }}> *</span>}
                                        </label>

                                        {field.type === 'long_text' ? (
                                            <textarea
                                                id={`zf_${field.key}`}
                                                rows={3}
                                                placeholder={field.placeholder}
                                                value={answers[field.key] || ''}
                                                onChange={(e) => {
                                                    setAnswers((prev) => ({ ...prev, [field.key]: e.target.value }));
                                                    setFieldErrors((prev) => ({ ...prev, [field.key]: '' }));
                                                }}
                                                style={{
                                                    // width: '100%', borderRadius: 10,
                                                    width: '100%', borderRadius: RADIUS, background: '#fff', boxSizing: 'border-box',
                                                    border: `1px solid ${fieldErrors[field.key] ? '#ef4444' : '#e5e7eb'}`,
                                                    padding: '12px 14px', fontSize: '1rem', fontFamily: 'inherit', resize: 'vertical',
                                                }}
                                            />
                                        ) : (
                                            <input
                                                id={`zf_${field.key}`}
                                                {...inputPropsFor(field.type)}
                                                placeholder={
                                                    field.type === 'phone'
                                                        ? (field.placeholder || 'e.g. +91 98765 43210')
                                                        : field.placeholder
                                                }
                                                value={answers[field.key] || ''}
                                                onChange={(e) => {
                                                    // Wrong characters never land in the field at all.
                                                    const value = sanitiseFor(field.type, e.target.value);
                                                    setAnswers((prev) => ({ ...prev, [field.key]: value }));
                                                    setFieldErrors((prev) => ({ ...prev, [field.key]: '' }));
                                                }}
                                                onBlur={() =>
                                                    setFieldErrors((prev) => ({
                                                        ...prev,
                                                        [field.key]: validateField(field, answers[field.key] || ''),
                                                    }))
                                                }
                                                style={{
                                                    // width: '100%', borderRadius: 10,
                                                    width: '100%', borderRadius: RADIUS, background: '#fff', boxSizing: 'border-box',
                                                    border: `1px solid ${fieldErrors[field.key] ? '#ef4444' : '#e5e7eb'}`,
                                                    // 16px keeps iOS Safari from zooming the page on focus.
                                                    padding: '12px 14px', fontSize: '1rem', fontFamily: 'inherit',
                                                }}
                                            />
                                        )}

                                        {!!fieldErrors[field.key] && (
                                            <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                                                {fieldErrors[field.key]}
                                            </p>
                                        )}
                                    </Box>
                                ))}

                                {!!formError && (
                                    <p style={{ color: '#ef4444', fontSize: '0.8125rem', marginBottom: 12 }}>{formError}</p>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        // width: '100%', border: 'none', borderRadius: 999, padding: '13px 16px',
                                        // background: submitting ? '#a78bfa' : ACCENT, color: '#fff',
                                        // Same dark rounded button as the bio page's link blocks.
                                        width: '100%', border: 'none', borderRadius: RADIUS, padding: '14px 16px', marginTop: 4,
                                        background: ACCENT, opacity: submitting ? 0.6 : 1, color: THEME.buttonText,
                                        boxShadow: `0 2px 10px ${ACCENT}30`,
                                        fontSize: '0.9375rem', fontWeight: 700, cursor: submitting ? 'default' : 'pointer',
                                    }}
                                >
                                    {submitting ? 'Sending…' : 'Submit'}
                                </button>
                            </form>
                        </>
                    )}
                </Box>

                <p style={{ textAlign: 'center', fontSize: '0.6875rem', color: '#9ca3af', marginTop: 24, marginBottom: 0 }}>
                    Powered by ClanConnect
                </p>
            </Box>
        </Box>
    );
};

export default PublicZapForm;
